import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import relativeTime from 'dayjs/plugin/relativeTime';
import { locale, t } from './i18n';

dayjs.locale(locale === 'en-US' ? 'en' : 'zh-cn');
dayjs.extend(relativeTime);

type DateInput = string | number | Date | null | undefined;

/**
 * `dayjs(undefined)` is *now*, not an invalid date, so every formatter has to reject the
 * empty cases first: a row whose `createdAt` is missing must render a dash, not the current
 * time. `dayjs(null)` and `dayjs('')` are "now" for the same reason.
 */
const toDayjs = (value: DateInput): dayjs.Dayjs | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

/** `2026-09-30 10:48`; an em dash for a missing or unparsable value. */
export const formatDateTime = (value: DateInput): string =>
  toDayjs(value)?.format('YYYY-MM-DD HH:mm') ?? '—';

export const formatDate = (value: DateInput): string =>
  toDayjs(value)?.format('YYYY-MM-DD') ?? '—';

/** `3 天前`: the column an operator scans instead of doing date arithmetic. */
export const fromNow = (value: DateInput): string =>
  toDayjs(value)?.fromNow() ?? '—';

/** `2 天 3 小时`: an uptime an operator can read at a glance. */
export const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return t('{{days}} 天 {{hours}} 小时', { days, hours });
  if (hours > 0) return t('{{hours}} 小时 {{minutes}} 分', { hours, minutes });
  if (minutes > 0)
    return t('{{minutes}} 分 {{seconds}} 秒', {
      minutes,
      seconds: Math.floor(seconds % 60),
    });
  return t('{{seconds}} 秒', { seconds: Math.floor(seconds) });
};

export const formatNumber = (value: number): string =>
  new Intl.NumberFormat(locale).format(value);

/**
 * One or two characters for an avatar fallback.
 *
 * A Chinese name has no spaces, so `split(' ')` would hand back the whole name: the
 * first character is the family name and reads better than a truncated blob.
 */
export const initialsOf = (name: string): string => {
  const trimmed = name.trim();
  if (trimmed === '') return '?';
  const words = trimmed.split(/\s+/);
  if (words.length === 1)
    return trimmed
      .slice(0, /[\u4e00-\u9fa5]/.test(trimmed) ? 1 : 2)
      .toUpperCase();
  return words
    .slice(0, 2)
    .map((word) => word.slice(0, 1))
    .join('')
    .toUpperCase();
};
